import type { PostHogConfig } from "posthog-js";

export function telemetryUrl(value: string): string {
  try {
    const url = new URL(value);
    if (!/^https?:$/.test(url.protocol)) return "[redacted]";
    return `${url.origin}${url.pathname}`;
  } catch {
    return "[redacted]";
  }
}

export function browserTelemetryEnvironment(hostname: string): string {
  // A production Next build is also used on private previews.
  if (hostname.endsWith(".seb.rockethouse.dev")) return "development";
  return process.env.NEXT_PUBLIC_APP_ENV || "unconfigured";
}

function redactUrlProperties(properties: Record<string, unknown>) {
  for (const key of [
    "$current_url",
    "$referrer",
    "$initial_current_url",
    "$initial_referrer",
  ]) {
    if (typeof properties[key] === "string") {
      properties[key] = telemetryUrl(properties[key]);
    }
  }
}

export function browserPostHogConfig(hostname: string): Partial<PostHogConfig> {
  return {
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: false,
    person_profiles: "identified_only",
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    // Enable only after the replay privacy acceptance drill. Text masking alone
    // does not cover sensitive values in arbitrary DOM attributes or images.
    disable_session_recording: true,
    enable_recording_console_log: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "*",
      recordHeaders: false,
      recordBody: false,
      maskCapturedNetworkRequestFn: (request) => ({
        ...request,
        name: telemetryUrl(request.name),
      }),
    },
    before_send: (event) => {
      if (!event) return null;
      if (event.event === "$exception") {
        const exceptions = event.properties.$exception_list;
        if (!Array.isArray(exceptions)) return null;
        if (
          exceptions.length &&
          exceptions.every((exception) =>
            /^(Unauthorized|Forbidden)$/.test(exception.value),
          )
        )
          return null;
        // Autocaptured errors bypass our manual reporting helper. Scrub here as
        // well, before any payload leaves the browser.
        event.properties.$exception_list = exceptions.map((exception) => ({
          type: /^(Error|TypeError|RangeError|ReferenceError|SyntaxError|URIError|AggregateError)$/.test(
            exception.type,
          )
            ? exception.type
            : "Error",
          value: "Unexpected application failure",
          mechanism: {
            handled: exception.mechanism?.handled === true,
            type: [
              "generic",
              "onunhandledrejection",
              "onuncaughtexception",
              "onconsole",
              "middleware",
            ].includes(exception.mechanism?.type)
              ? exception.mechanism.type
              : "generic",
          },
          stacktrace: {
            type: "raw",
            frames: (exception.stacktrace?.frames || []).map(
              (frame: Record<string, unknown>) => ({
                platform: "web:javascript",
                chunk_id:
                  typeof frame.chunk_id === "string" &&
                  /^[a-f0-9-]{32,36}$/i.test(frame.chunk_id)
                    ? frame.chunk_id
                    : undefined,
                filename:
                  typeof frame.filename === "string"
                    ? frame.filename.startsWith("http")
                      ? telemetryUrl(frame.filename)
                      : frame.filename.split(/[?#]/)[0]
                    : undefined,
                function:
                  typeof frame.function === "string"
                    ? frame.function
                        .replace(/[^a-zA-Z0-9_.$<> ]/g, "_")
                        .slice(0, 160)
                    : undefined,
                lineno:
                  typeof frame.lineno === "number" ? frame.lineno : undefined,
                colno:
                  typeof frame.colno === "number" ? frame.colno : undefined,
                in_app: frame.in_app === true,
              }),
            ),
          },
        }));
        for (const key of Object.keys(event.properties)) {
          if (
            key.startsWith("$exception_") &&
            ![
              "$exception_list",
              "$exception_handled",
              "$exception_level",
            ].includes(key)
          )
            delete event.properties[key];
        }
      }
      redactUrlProperties(event.properties);
      for (const key of ["$set", "$set_once"]) {
        const properties = event.properties[key];
        if (properties && typeof properties === "object")
          redactUrlProperties(properties);
      }
      event.properties.environment = browserTelemetryEnvironment(hostname);
      event.properties.service = "nextjs-browser";
      event.properties.release =
        process.env.NEXT_PUBLIC_RELEASE_SHA || "unconfigured";
      return event;
    },
  };
}
